/**
 * emailDeliveryRecentBroadcastPerformanceApi
 *
 * RECENT-BROADCAST-PERFORMANCE-001: client for the real, read-only Recent
 * Broadcast Performance Dashboard API
 * (`GET /api/email-delivery/dashboard/recent-broadcast-performance`).
 *
 * Wraps the SAME shared `api` axios instance every other real-backed War
 * Room surface already uses (see lib/api.js, emailDeliveryDashboardApi.js,
 * emailDeliveryLpeFunnelApi.js) -- no second HTTP stack. Auth/tenant
 * scoping are attached automatically by that instance's own request
 * interceptor; this module never accepts or forwards a caller-supplied
 * tenant id.
 *
 * ONE ROW PER GOVERNED BROADCAST -- never an individual recipient/
 * delivery-event/LPE-event row. `period` is one of the canonical named
 * period keys `resolve_dashboard_reporting_period` (backend) accepts --
 * the SAME named values the existing Email Dashboard Period control
 * already uses. `startDate`/`endDate` (YYYY-MM-DD) are forwarded only
 * when `period === "custom"`.
 *
 * `broadcastId`/`audienceId`/`campaignId`/`programId`/`state` are the SAME
 * governed filters every other real Email Dashboard endpoint accepts --
 * always the stable governed operational ID (BRC####/AUD###/CMP####/
 * PRG###) or normalized two-letter state/DC code, never a mutable display
 * name.
 *
 * `observedGhlEnvironment` (EDT discovery-source provenance) and
 * `environment` (the governed Broadcast's own intended environment,
 * LPE-side) are deliberately TWO SEPARATE parameters -- the SAME
 * non-conflation contract emailDeliveryLpeFunnelApi.js's own docstring
 * documents. Never send one value under the other's query parameter name.
 *
 * `limit` bounds the number of rows returned (backend default 10, max 25).
 */
import { api } from "./api";

export async function getEmailDeliveryRecentBroadcastPerformance({
  period = "mtd",
  startDate,
  endDate,
  broadcastId,
  audienceId,
  campaignId,
  programId,
  observedGhlEnvironment,
  environment,
  state,
  limit,
} = {}) {
  const params = { period };
  if (period === "custom") {
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
  }
  if (broadcastId) params.broadcast_id = broadcastId;
  if (audienceId) params.audience_id = audienceId;
  if (campaignId) params.campaign_id = campaignId;
  if (programId) params.program_id = programId;
  if (observedGhlEnvironment && observedGhlEnvironment !== "all") {
    params.observed_ghl_environment = observedGhlEnvironment;
  }
  if (environment && environment !== "all") params.environment = environment;
  if (state) params.state = state;
  if (typeof limit === "number") params.limit = limit;
  const res = await api.get("/email-delivery/dashboard/recent-broadcast-performance", { params });
  return res.data;
}

export default getEmailDeliveryRecentBroadcastPerformance;
